import { useRef } from "react";
import { motion, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowRight, Menu } from "lucide-react";
import { LogoMark } from "@/components/Logo";
import { Reveal } from "@/components/Reveal";
import { useSectionNav } from "@/lib/scroll";

const EASE = [0.16, 1, 0.3, 1];

const TemplateCard = ({ className = "", style }) => (
  <motion.div
    style={style}
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.3 }}
    transition={{ duration: 1, ease: EASE }}
    className={`shrink-0 rounded-xl border border-white/5 bg-[#131315] p-5 ${className}`}
    aria-hidden="true"
  >
    <div className="flex items-center justify-between mb-5">
      <span className="mono text-[9px] tracking-[0.25em] uppercase text-neutral-600">Template</span>
      <Menu size={12} className="text-neutral-700" />
    </div>
    <p className="font-display font-bold text-neutral-600 text-lg leading-snug">
      Your Business
      <br />
      Online
    </p>
    <p className="text-[10px] text-neutral-700 mt-2">A simple and effective solution for your business.</p>
    <div className="mt-4 w-20 h-6 rounded-full bg-white/5 flex items-center justify-center">
      <span className="text-[8px] text-neutral-600">Get Started</span>
    </div>
    <div className="grid grid-cols-2 gap-2 mt-5">
      <div className="h-12 rounded bg-white/5" />
      <div className="h-12 rounded bg-white/5" />
    </div>
    <div className="mt-3 space-y-1.5">
      <div className="h-1.5 rounded bg-white/5 w-full" />
      <div className="h-1.5 rounded bg-white/5 w-4/5" />
      <div className="h-1.5 rounded bg-white/5 w-3/5" />
    </div>
  </motion.div>
);

export const TemplateShowcase = () => {
  const go = useSectionNav();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const xLeftFar = useSpring(useTransform(scrollYProgress, [0, 1], [60, -46]), { stiffness: 50, damping: 20 });
  const xLeft = useSpring(useTransform(scrollYProgress, [0, 1], [34, -26]), { stiffness: 50, damping: 20 });
  const xRight = useSpring(useTransform(scrollYProgress, [0, 1], [-34, 26]), { stiffness: 50, damping: 20 });
  const xRightFar = useSpring(useTransform(scrollYProgress, [0, 1], [-60, 46]), { stiffness: 50, damping: 20 });
  const centerY = useSpring(useTransform(scrollYProgress, [0, 1], [26, -26]), { stiffness: 50, damping: 20 });

  return (
    <section
      ref={ref}
      data-testid="template-showcase-section"
      className="relative py-28 lg:py-40 px-5 sm:px-8 overflow-hidden border-t border-white/5"
    >
      <div className="max-w-7xl mx-auto">
        <Reveal className="text-center mb-14 lg:mb-20">
          <p className="mono text-[11px] tracking-[0.28em] uppercase text-neutral-500">Bespoke, Never Templated</p>
        </Reveal>

        <div className="relative flex items-center justify-center [perspective:1400px]">
          <div
            className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[60%] h-24 bg-white/[0.06] blur-[70px] rounded-full pointer-events-none"
            aria-hidden="true"
          />
          <div className="flex items-center justify-center [transform-style:preserve-3d]">
            <TemplateCard
              style={{ x: xLeftFar }}
              className="hidden lg:block w-56 -mr-14 opacity-60 [transform:rotateY(30deg)_translateZ(-120px)]"
            />
            <TemplateCard
              style={{ x: xLeft }}
              className="hidden sm:block w-60 lg:w-64 -mr-12 lg:-mr-16 opacity-80 [transform:rotateY(24deg)_translateZ(-50px)]"
            />

            <motion.div
              style={{ y: centerY }}
              className="relative z-10 w-[300px] sm:w-[400px] lg:w-[480px] shrink-0"
            >
              <motion.div
                animate={{ rotateY: [0, 2.5, 0, -2.5, 0], rotateX: [0, -1.5, 0, 1.5, 0] }}
                transition={{ repeat: Infinity, duration: 14, ease: "easeInOut" }}
              >
                <div className="rounded-xl border border-white/20 bg-[#0e0e10] shadow-[0_0_90px_-10px_rgba(255,255,255,0.15)] overflow-hidden">
                  <div className="flex items-center justify-between px-5 py-3.5 border-b border-white/10">
                    <span className="flex items-center gap-2">
                      <LogoMark size={16} />
                      <span className="mono text-[9px] tracking-[0.25em] text-neutral-400">NOVO</span>
                    </span>
                    <span className="hidden sm:flex gap-3 mono text-[8px] tracking-[0.2em] text-neutral-600">
                      <span>HOME</span>
                      <span>SERVICES</span>
                      <span>WORK</span>
                      <span>CONTACT</span>
                    </span>
                  </div>
                  <div className="p-7 lg:p-10">
                    <h2 className="font-display font-bold tracking-tight text-neutral-50 text-2xl lg:text-[2rem] leading-[1.1]">
                      A template can't tell your story.
                    </h2>
                    <p className="text-neutral-400 text-xs lg:text-sm leading-relaxed mt-4 max-w-xs">
                      Your website is often the first impression someone has of your business. Make it count.
                    </p>
                    <button
                      data-testid="template-showcase-cta"
                      onClick={() => go("/contact")}
                      className="group mt-6 inline-flex items-center gap-2 rounded-full bg-neutral-50 text-[#0a0a0b] text-xs font-medium pl-4 pr-3 py-2 hover:bg-white transition-colors duration-300"
                    >
                      Start a Project
                      <ArrowRight size={13} className="transition-transform duration-300 group-hover:translate-x-0.5" />
                    </button>
                  </div>
                  <div className="flex items-center justify-between px-5 py-3 border-t border-white/10">
                    <span className="mono text-[8px] tracking-[0.25em] text-neutral-600">CUSTOM DESIGN</span>
                    <span className="mono text-[8px] tracking-[0.25em] text-neutral-600">NOVO DIGITAL STUDIO</span>
                  </div>
                </div>
              </motion.div>
            </motion.div>

            <TemplateCard
              style={{ x: xRight }}
              className="hidden sm:block w-60 lg:w-64 -ml-12 lg:-ml-16 opacity-80 [transform:rotateY(-24deg)_translateZ(-50px)]"
            />
            <TemplateCard
              style={{ x: xRightFar }}
              className="hidden lg:block w-56 -ml-14 opacity-60 [transform:rotateY(-30deg)_translateZ(-120px)]"
            />
          </div>
        </div>
      </div>
    </section>
  );
};
